/**
 * Types for the `getWeather` Cloud Function callable (ADR-096).
 *
 * The callable proxies Apple WeatherKit REST API through a server-side
 * JWT-authenticated request with Firestore caching. Clients never call
 * WeatherKit directly.
 */

import { z } from 'zod';

/** Granularity of weather data requested. */
export const weatherGranularitySchema = z.enum(['current', 'hourly', 'daily']);
export type WeatherGranularity = z.infer<typeof weatherGranularitySchema>;

export const getWeatherInputSchema = z
  .object({
    lat: z.number(),
    lng: z.number(),
    /** Defaults to 'current' if omitted. */
    when: weatherGranularitySchema.optional(),
    /** IANA timezone for day-boundary alignment (e.g. "America/Denver"). */
    timezone: z.string().optional(),
  })
  .strict();

export type GetWeatherInput = z.infer<typeof getWeatherInputSchema>;

/** Normalized current conditions from WeatherKit. */
export interface CurrentWeather {
  /** Temperature in Celsius. */
  temperature: number;
  /** Feels-like temperature in Celsius. */
  temperatureApparent: number;
  /** WeatherKit condition code (e.g. "PartlyCloudy", "Snow", "Clear"). */
  conditionCode: string;
  /** Human-readable condition label. */
  conditionLabel: string;
  /** Relative humidity 0-1. */
  humidity: number;
  /** Wind speed in km/h. */
  windSpeed: number;
  /** UV index. */
  uvIndex: number;
  /** ISO 8601 timestamp of the observation. */
  asOf: string;
}

/** One day in the daily forecast. */
export interface DailyForecast {
  /** ISO 8601 date string (YYYY-MM-DD). */
  forecastDate: string;
  conditionCode: string;
  conditionLabel: string;
  /** High temperature in Celsius. */
  temperatureMax: number;
  /** Low temperature in Celsius. */
  temperatureMin: number;
  /** Precipitation probability 0-1. */
  precipitationChance: number;
  /** ISO 8601 sunrise time. */
  sunrise: string;
  /** ISO 8601 sunset time. */
  sunset: string;
}

/** One hour in the hourly forecast. */
export interface HourlyForecast {
  /** ISO 8601 timestamp. */
  forecastStart: string;
  temperature: number;
  temperatureApparent: number;
  conditionCode: string;
  conditionLabel: string;
  humidity: number;
  precipitationChance: number;
  windSpeed: number;
}

export interface GetWeatherResponse {
  current?: CurrentWeather;
  daily?: DailyForecast[];
  hourly?: HourlyForecast[];
  /** Whether this result was served from cache. */
  cached: boolean;
  /** Apple WeatherKit attribution URL (required by TOS). */
  attribution: string;
}
